import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import { ArrowUpDown, ExternalLink, Eye, Heart, Bookmark, MessageSquare, FileText } from 'lucide-react';

interface Row {
  id: string;
  slug: string | null;
  title: any;
  category: string | null;
  view_count: number;
  published_at: string;
  is_published: boolean;
  likes: number;
  bookmarks: number;
  comments: number;
}

type SortKey = 'title' | 'view_count' | 'likes' | 'bookmarks' | 'comments' | 'published_at';

function getEn(field: any): string {
  if (!field) return '';
  if (typeof field === 'string') return field;
  return field.en || (Object.values(field)[0] as string) || '';
}

export default function BlogAnalytics() {
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('view_count');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const { data: rows, isLoading } = useQuery({
    queryKey: ['blog-analytics-rows'],
    queryFn: async (): Promise<Row[]> => {
      const { data: posts } = await supabase
        .from('blog_posts')
        .select('id, slug, title, category, view_count, published_at, is_published')
        .order('published_at', { ascending: false });
      if (!posts) return [];
      const ids = posts.map((p) => p.id);
      const [reactions, comments] = await Promise.all([
        supabase.from('blog_reactions').select('post_id, kind').in('post_id', ids),
        supabase.from('blog_comments').select('post_id').in('post_id', ids),
      ]);
      const likeMap = new Map<string, number>();
      const bookmarkMap = new Map<string, number>();
      const commentMap = new Map<string, number>();
      for (const r of reactions.data || []) {
        const m = r.kind === 'like' ? likeMap : bookmarkMap;
        m.set(r.post_id, (m.get(r.post_id) || 0) + 1);
      }
      for (const c of comments.data || []) {
        commentMap.set(c.post_id, (commentMap.get(c.post_id) || 0) + 1);
      }
      return posts.map((p) => ({
        ...p,
        likes: likeMap.get(p.id) || 0,
        bookmarks: bookmarkMap.get(p.id) || 0,
        comments: commentMap.get(p.id) || 0,
      })) as Row[];
    },
  });

  const { data: trend } = useQuery({
    queryKey: ['blog-analytics-trend'],
    queryFn: async () => {
      const since = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
      const { data } = await supabase
        .from('blog_view_events')
        .select('viewed_at')
        .gte('viewed_at', since)
        .limit(10000);
      const buckets = new Map<string, number>();
      for (let i = 29; i >= 0; i--) {
        const d = new Date(Date.now() - i * 24 * 60 * 60 * 1000);
        buckets.set(d.toISOString().slice(0, 10), 0);
      }
      for (const v of data || []) {
        const day = v.viewed_at.slice(0, 10);
        if (buckets.has(day)) buckets.set(day, (buckets.get(day) || 0) + 1);
      }
      return [...buckets.entries()].map(([date, views]) => ({
        date: date.slice(5),
        views,
      }));
    },
  });

  const totals = useMemo(() => {
    const r = rows || [];
    return {
      posts: r.length,
      published: r.filter((x) => x.is_published).length,
      views: r.reduce((s, x) => s + (x.view_count || 0), 0),
      likes: r.reduce((s, x) => s + x.likes, 0),
      bookmarks: r.reduce((s, x) => s + x.bookmarks, 0),
      comments: r.reduce((s, x) => s + x.comments, 0),
    };
  }, [rows]);

  const filteredSorted = useMemo(() => {
    const r = (rows || []).filter((x) => {
      if (!search.trim()) return true;
      const t = getEn(x.title).toLowerCase();
      return t.includes(search.toLowerCase()) || (x.category || '').toLowerCase().includes(search.toLowerCase());
    });
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...r].sort((a, b) => {
      let av: any, bv: any;
      if (sortKey === 'title') { av = getEn(a.title).toLowerCase(); bv = getEn(b.title).toLowerCase(); }
      else if (sortKey === 'published_at') { av = a.published_at; bv = b.published_at; }
      else { av = (a as any)[sortKey]; bv = (b as any)[sortKey]; }
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [rows, search, sortKey, sortDir]);

  const onSort = (k: SortKey) => {
    if (sortKey === k) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortKey(k); setSortDir(k === 'title' ? 'asc' : 'desc'); }
  };

  return (
    <div className="container max-w-7xl py-6 space-y-6">
      <div>
        <h1 className="font-display text-2xl font-bold">Blog Analytics</h1>
        <p className="text-sm text-muted-foreground">Engagement metrics across all blog posts.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <KpiCard icon={FileText} label="Published posts" value={totals.published} sub={`${totals.posts} total`} />
        <KpiCard icon={Eye} label="Total views" value={totals.views} />
        <KpiCard icon={Heart} label="Likes" value={totals.likes} />
        <KpiCard icon={Bookmark} label="Bookmarks" value={totals.bookmarks} />
        <KpiCard icon={MessageSquare} label="Comments" value={totals.comments} />
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Views over the last 30 days</CardTitle></CardHeader>
        <CardContent className="h-[260px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={trend || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={11} />
              <YAxis allowDecimals={false} stroke="hsl(var(--muted-foreground))" fontSize={11} />
              <Tooltip
                contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 12 }}
              />
              <Line type="monotone" dataKey="views" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <CardTitle className="text-base">Per-post performance</CardTitle>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter by title or category…"
            className="max-w-xs"
          />
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {isLoading ? (
            <div className="text-center text-sm text-muted-foreground py-10">Loading…</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead><SortBtn label="Title" k="title" sortKey={sortKey} sortDir={sortDir} onSort={onSort} /></TableHead>
                  <TableHead className="text-right"><SortBtn label="Views" k="view_count" sortKey={sortKey} sortDir={sortDir} onSort={onSort} /></TableHead>
                  <TableHead className="text-right"><SortBtn label="Likes" k="likes" sortKey={sortKey} sortDir={sortDir} onSort={onSort} /></TableHead>
                  <TableHead className="text-right"><SortBtn label="Bookmarks" k="bookmarks" sortKey={sortKey} sortDir={sortDir} onSort={onSort} /></TableHead>
                  <TableHead className="text-right"><SortBtn label="Comments" k="comments" sortKey={sortKey} sortDir={sortDir} onSort={onSort} /></TableHead>
                  <TableHead><SortBtn label="Published" k="published_at" sortKey={sortKey} sortDir={sortDir} onSort={onSort} /></TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredSorted.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="max-w-[280px]">
                      <div className="font-medium truncate">{getEn(r.title) || '(untitled)'}</div>
                      <div className="text-[11px] text-muted-foreground">
                        {r.category || 'Uncategorized'}{!r.is_published && ' · draft'}
                      </div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{r.view_count}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.likes}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.bookmarks}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.comments}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {new Date(r.published_at).toLocaleDateString('en-GB')}
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" className="h-7 w-7" asChild>
                        <Link to={`/blogs/${r.slug || r.id}`} target="_blank" rel="noopener">
                          <ExternalLink className="h-3.5 w-3.5" />
                        </Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredSorted.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-8">No posts match.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, sub }: { icon: any; label: string; value: number; sub?: string }) {
  return (
    <Card>
      <CardContent className="pt-5">
        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
          <Icon className="h-3.5 w-3.5" /> {label}
        </div>
        <div className="text-2xl font-bold tabular-nums">{value.toLocaleString()}</div>
        {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
      </CardContent>
    </Card>
  );
}

function SortBtn({ label, k, sortKey, sortDir, onSort }: {
  label: string; k: SortKey; sortKey: SortKey; sortDir: 'asc' | 'desc'; onSort: (k: SortKey) => void;
}) {
  const active = sortKey === k;
  return (
    <button
      onClick={() => onSort(k)}
      className={`inline-flex items-center gap-1 hover:text-foreground transition ${active ? 'text-foreground font-semibold' : ''}`}
    >
      {label}
      <ArrowUpDown className={`h-3 w-3 ${active ? '' : 'opacity-40'}`} />
      {active && <span className="text-[9px]">{sortDir === 'asc' ? '↑' : '↓'}</span>}
    </button>
  );
}
